import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DelayedUserInputService } from '../circular-slider/services/delayed-user-input.service';
import { GraphicBehaviorType } from '@caslink/device-client/ng-app/shared/models/graphic-behavior-type.enum';
import { ThemeService } from '@caslink/common-client/ng-app/theme/ThemeService';

const FULL_CIRCLE_DEGREES = 360;
const ANGLE_OFFSET = 5;

enum HeatingCooling {
    HEATING = 'HEATING',
    COOLING = 'COOLING'
}

@Component({
    selector: 'dl-nest-slider',
    templateUrl: './nest-slider.component.html',
    styleUrls: ['./nest-slider.component.scss'],
    providers: [DelayedUserInputService],
})
export class NestSliderComponent implements OnInit, OnDestroy {
    @Input()
    get setpointMinimum(): number {
        return !isNaN(this._setpointMinimum) ? this.adjustMinimumSetpoint(this._setpointMinimum) : undefined;
    }

    set setpointMinimum(value: number) {
        this._setpointMinimum = value;
    }

    @Input()
    get setpointMaximum(): number {
        return !isNaN(this._setpointMaximum) ? this.adjustMaximumSetpoint(this._setpointMaximum) : undefined;
    }

    set setpointMaximum(value: number) {
        this._setpointMaximum = value;
    }

    @Input() temperature = '';
    @Input() relativeHumidity = '';

    @Input() size = 200;
    @Input() minAngle = 230;
    @Input() maxAngle = 130;
    @Input() handleDiameter = 30;

    @Input() handleOriginTransformX = 0;
    @Input() handleOriginTransformY = 0;

    @Input() whiteMode = false;
    @Input() enabled;

    @Input()
    get coolingSetpoint(): number {
        return !isNaN(this._coolingSetpoint) ? Math.round(this._coolingSetpoint) : undefined;
    }

    set coolingSetpoint(value: number) {
        this._coolingSetpoint = value;
        if (!isNaN(this._coolingSetpoint)) {
            this.coolingSetpointChange.emit(this._coolingSetpoint);
        }
    }

    @Input()
    get heatingSetpoint(): number {
        return !isNaN(this._heatingSetpoint) ? Math.round(this._heatingSetpoint) : undefined;
    }

    set heatingSetpoint(value: number) {
        this._heatingSetpoint = value;
        if (!isNaN(this._heatingSetpoint)) {
            this.heatingSetpointChange.emit(this._heatingSetpoint);
        }
    }

    get angleHeatingWithOffset() {
        const angleHeatingOffset = this.angleHeating - ANGLE_OFFSET;
        if (angleHeatingOffset > this.minAngle) {
            return Math.max(this.minAngle, angleHeatingOffset);
        } else if (Math.abs(this.minAngle - this.angleHeating) < ANGLE_OFFSET) {
            return this.minAngle;
        } else {
            return Math.min(this.minAngle, angleHeatingOffset);
        }
    }

    get angleCoolingWithOffset() {
        const angleCoolingOffset = this.angleCooling + ANGLE_OFFSET;
        if (angleCoolingOffset < this.maxAngle) {
            return Math.min(this.maxAngle, angleCoolingOffset);
        } else if (Math.abs(this.angleCooling - this.maxAngle) < ANGLE_OFFSET) {
            return this.maxAngle;
        } else {
            return Math.max(this.maxAngle, angleCoolingOffset);
        }
    }

    @Output() coolingSetpointChange = new EventEmitter<number>();
    @Output() heatingSetpointChange = new EventEmitter<number>();
    @Output() coolingSetpointDebouncedChange = new EventEmitter<number>();
    @Output() heatingSetpointDebouncedChange = new EventEmitter<number>();

    get r(): number {
        return this.size / 2 - this.handleDiameter / 2;
    }

    get cx(): number {
        return this.size / 2;
    }

    get cy(): number {
        return this.size / 2;
    }

    get angleCooling(): number {
        return this.convertTemperatureToAngle(this.coolingSetpoint);
    }

    set angleCooling(newAngle) {
        this.coolingSetpoint = this.convertAngleToTemperature(newAngle);
    }

    get angleHeating(): number {
        return this.convertTemperatureToAngle(this.heatingSetpoint);
    }

    set angleHeating(newAngle) {
        this.heatingSetpoint = this.convertAngleToTemperature(newAngle);
    }

    get minAngleHeating(): number {
        return this.minAngle;
    }

    get maxAngleHeating(): number {
        if (this.coolingSetpoint === undefined) {
            return this.convertTemperatureToAngle(this.setpointMaximum);
        }
        return this.convertTemperatureToAngle(this.coolingSetpoint - 2);
    }

    get minAngleCooling(): number {
        if (this.heatingSetpoint === undefined) {
            return this.convertTemperatureToAngle(this.setpointMinimum);
        }
        return this.convertTemperatureToAngle(this.heatingSetpoint + 2);
    }

    get maxAngleCooling(): number {
        return this.maxAngle;
    }

    get temperatureToDisplay(): string {
        if (this.displayHeatingTemperature) {
            return this.heatingSetpoint + '°';
        }
        if (this.displayCoolingTemperature) {
            return this.coolingSetpoint + '°';
        }
        return this.temperature;
    }

    get centerClass() {
        return {'top': this.size / 2 + 'px', 'left': this.size / 2 + 'px'};
    }

    displayCoolingTemperature: boolean;
    displayHeatingTemperature: boolean;
    HeatingCooling = HeatingCooling;
    relativeHumiditySensorIcon = GraphicBehaviorType.RELATIVE_HUMIDITY_SENSOR as string;

    private initialHeatingSetpoint;
    private initialCoolingSetpoint;
    private _heatingSetpoint = 40;
    private _coolingSetpoint = 60;
    private _setpointMinimum = 0;
    private _setpointMaximum = 100;
    private timeoutHandler: NodeJS.Timer;
    private readonly PRESS_AND_HOLD_INTERVAL_MS = 150;

    constructor(private cd: ChangeDetectorRef,
                private delayedUserInputService: DelayedUserInputService,
                private themeService: ThemeService,
                ) {
    }

    ngOnInit() {
        this.initialHeatingSetpoint = Math.round(this.heatingSetpoint);
        this.initialCoolingSetpoint = Math.round(this.coolingSetpoint);

        this.delayedUserInputService.currentUserInput.subscribe(ui => {
            if (ui === HeatingCooling.HEATING) {
                this.heatingHandleMovingStarted();
            } else if (ui === HeatingCooling.COOLING) {
                this.coolingHandleMovingStarted();
            }
        });
        this.delayedUserInputService.userInputFinished.subscribe(ui => {
            if (ui === HeatingCooling.HEATING) {
                this.displayHeatingTemperature = false;
                this.heatingSetpointDebouncedChange.emit(this._heatingSetpoint);
            } else if (ui === HeatingCooling.COOLING) {
                this.displayCoolingTemperature = false;
                this.coolingSetpointDebouncedChange.emit(this._coolingSetpoint);
            }
        });
    }

    ngOnDestroy(): void {
        this.stopIncDecTimer();
    }

    rotate(angle: number): string {
        return `rotate(${angle})`;
    }

    decreaseHeating(event): void {
        event.stopPropagation();
        event.preventDefault();
        this.tryToDecreaseHeating();
        this.timeoutHandler = setInterval(() => {
            this.tryToDecreaseHeating();
        }, this.PRESS_AND_HOLD_INTERVAL_MS);
    }

    increaseHeating(event): void {
        event.stopPropagation();
        event.preventDefault();
        this.tryToIncreaseHeating();
        this.timeoutHandler = setInterval(() => {
            this.tryToIncreaseHeating();
        }, this.PRESS_AND_HOLD_INTERVAL_MS);
    }

    decreaseCooling(event): void {
        event.stopPropagation();
        event.preventDefault();
        this.tryToDecreaseCooling();
        this.timeoutHandler = setInterval(() => {
            this.tryToDecreaseCooling();
        }, this.PRESS_AND_HOLD_INTERVAL_MS);
    }

    increaseCooling(event): void {
        event.stopPropagation();
        event.preventDefault();
        this.tryToIncreaseCooling();
        this.timeoutHandler = setInterval(() => {
            this.tryToIncreaseCooling();
        }, this.PRESS_AND_HOLD_INTERVAL_MS);
    }

    getNumberOfTicks(): number {
        return Math.round(this.setpointMaximum - this.setpointMinimum);
    }

    coolingHandleMovingStarted() {
        this.displayCoolingTemperature = true;
        this.displayHeatingTemperature = false;
    }

    coolingHandleMovingFinished() {
        this.displayCoolingTemperature = false;
        this.cd.detectChanges();
    }

    heatingHandleMovingStarted() {
        this.displayHeatingTemperature = true;
        this.displayCoolingTemperature = false;
    }

    heatingHandleMovingFinished() {
        this.displayHeatingTemperature = false;
        this.cd.detectChanges();
    }

    stopIncDecTimer() {
        if (this.timeoutHandler) {
            clearTimeout(this.timeoutHandler);
            this.timeoutHandler = null;
        }
    }

    isDarkTheme() {
        return this.themeService.getTheme() === 'casDark';
    }

    private convertAngleToTemperature(angle: number): number {
        const temperatureRange = this.setpointMaximum - this.setpointMinimum;
        const normalCalc = Math.abs(this.maxAngle - this.minAngle);
        const adjustedCalc = FULL_CIRCLE_DEGREES - Math.abs(this.maxAngle - this.minAngle);
        const leftToRightDirection = Math.abs(angle - this.minAngle);
        const rightToLeftDirection = Math.abs(FULL_CIRCLE_DEGREES - this.minAngle + angle);

        const angleRange = this.minAngle < this.maxAngle ? normalCalc : adjustedCalc;
        const angleFromZero = angle >= this.minAngle ? leftToRightDirection : rightToLeftDirection;
        const temperatureFromMin = this.setpointMinimum + (angleFromZero * temperatureRange / angleRange);

        return Math.round(this.limitTemperature(Math.round(temperatureFromMin)));
    }

    private convertTemperatureToAngle(temperature: number): number {
        const temperatureRange = this.setpointMaximum - this.setpointMinimum;
        const normalCalc = Math.abs(this.maxAngle - this.minAngle);
        const adjustedCalc = FULL_CIRCLE_DEGREES - Math.abs(this.maxAngle - this.minAngle);

        const angleRange = this.minAngle < this.maxAngle ? normalCalc : adjustedCalc;
        const temperatureFromZero = temperature - this.setpointMinimum;
        const angleAbs = temperatureFromZero * angleRange / temperatureRange;

        const greaterThanCircleCalc = this.minAngle + angleAbs - FULL_CIRCLE_DEGREES;
        const smallerThanCircleCalc = this.minAngle + angleAbs;
        return Math.round((this.minAngle + angleAbs > FULL_CIRCLE_DEGREES) ? greaterThanCircleCalc : smallerThanCircleCalc);
    }

    private limitTemperature(t: number): number {
        if (t < this.setpointMinimum) {
            return this.setpointMinimum;
        }
        if (t > this.setpointMaximum) {
            return this.setpointMaximum;
        }
        return t;
    }

    private tryToIncreaseCooling() {
        if (!this.enabled) {
            return;
        }
        if (this.coolingSetpoint === undefined) {
            this.stopIncDecTimer();
            return;
        }
        if ((this.coolingSetpoint + 1) > this.setpointMaximum) {
            this.coolingSetpoint = this.setpointMaximum;
        } else {
            this.coolingSetpoint++;
        }
        this.delayedUserInputService.fireNextUserInput(HeatingCooling.COOLING);
    }

    private tryToDecreaseCooling() {
        if (!this.enabled) {
            return;
        }
        if (this.coolingSetpoint === undefined) {
            this.stopIncDecTimer();
            return;
        }
        if ((this.coolingSetpoint - 2) <= this.heatingSetpoint) {
            this.coolingSetpoint = this.heatingSetpoint + 2;
        } else if ((this.coolingSetpoint - 1) <= this.setpointMinimum) {
            this.coolingSetpoint = this.setpointMinimum;
        } else {
            this.coolingSetpoint--;
        }
        this.delayedUserInputService.fireNextUserInput(HeatingCooling.COOLING);
    }

    private tryToIncreaseHeating() {
        if (!this.enabled) {
            return;
        }
        if (this.heatingSetpoint === undefined) {
            this.stopIncDecTimer();
            return;
        }
        if ((this.heatingSetpoint + 2) >= this.coolingSetpoint) {
            this.heatingSetpoint = this.coolingSetpoint - 2;
        } else if ((this.heatingSetpoint + 1) >= this.setpointMaximum) {
            this.heatingSetpoint = this.setpointMaximum;
        } else {
            this.heatingSetpoint++;
        }
        this.delayedUserInputService.fireNextUserInput(HeatingCooling.HEATING);
    }

    private tryToDecreaseHeating() {
        if (!this.enabled) {
            return;
        }
        if (this.heatingSetpoint === undefined) {
            this.stopIncDecTimer();
            return;
        }
        if ((this.heatingSetpoint - 1) <= this.setpointMinimum) {
            this.heatingSetpoint = this.setpointMinimum;
        } else {
            this.heatingSetpoint--;
        }
        this.delayedUserInputService.fireNextUserInput(HeatingCooling.HEATING);
    }

    private adjustMinimumSetpoint(n: number) {
        if (this.heatingSetpoint) {
            n = Math.min(isNaN(this._setpointMinimum) ? Infinity : this._setpointMinimum, this.heatingSetpoint);
        }
        if (this.coolingSetpoint) {
            n = Math.min(isNaN(this._setpointMinimum) ? Infinity : this._setpointMinimum, this.coolingSetpoint);
        }
        return n;
    }
    private adjustMaximumSetpoint(n: number) {
        if (this.heatingSetpoint) {
            n = Math.max(isNaN(this._setpointMaximum) ? -Infinity : this._setpointMaximum, this.heatingSetpoint);
        }
        if (this.coolingSetpoint) {
            n = Math.max(isNaN(this._setpointMaximum) ? -Infinity : this._setpointMaximum, this.coolingSetpoint);
        }
        return n;
    }
}
